import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BookOpen,
  Download,
  Store,
  Code,
  Key,
  ChevronDown,
  ChevronRight,
  Monitor,
  UserPlus,
  Play,
  Search,
  CreditCard,
  FolderDown,
  Upload,
  Link as LinkIcon,
  DollarSign,
  BarChart3,
  RefreshCw,
  Cpu,
  Menu,
  X,
  Bot,
  Zap,
  List,
  AlertTriangle,
  Radio,
} from 'lucide-react';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    items: [
      {
        id: 'installing',
        title: 'Installing ContextUI',
        icon: Monitor,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              ContextUI is available for <strong className="text-white">Windows</strong>, <strong className="text-white">macOS</strong>, and <strong className="text-white">Linux</strong>. Download the latest version from{' '}
              <a href="https://contextui.ai/download" className="text-emerald-400 hover:text-emerald-300 underline">contextui.ai/download</a>.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Installation Steps</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Visit <span className="text-emerald-400">contextui.ai/download</span> and select your operating system.</li>
                <li>Download the installer package for your platform.</li>
                <li>Run the installer and follow the on-screen prompts.</li>
                <li>Launch ContextUI from your applications menu or desktop shortcut.</li>
              </ol>
            </div>
            <p className="text-gray-400 text-sm">
              On macOS, you may need to allow the app in <strong className="text-gray-300">System Settings → Privacy & Security</strong> on first launch. On Linux, install the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">.deb</code> package with <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">sudo dpkg -i contextui.deb</code>.
            </p>
          </>
        ),
      },
      {
        id: 'creating-account',
        title: 'Creating Your Account',
        icon: UserPlus,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Sign up for a free account at{' '}
              <a href="https://contextui.ai" className="text-emerald-400 hover:text-emerald-300 underline">contextui.ai</a>{' '}
              to access the Exchange, track your downloads, and manage your workflows.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Sign-Up Options</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Google</strong> — Sign in with your existing Google account.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Email & Password</strong> — Register with any email address and create a password.</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              Authentication is powered by WorkOS for enterprise-grade security. Your account works across the website and the desktop app.
            </p>
          </>
        ),
      },
      {
        id: 'first-workflow',
        title: 'Your First Workflow',
        icon: Play,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Once ContextUI is installed and you've signed in, you're ready to run your first workflow.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Quick Start</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Open ContextUI and explore the <strong className="text-white">built-in example workflows</strong> that come pre-installed.</li>
                <li>Browse the <strong className="text-white">Exchange</strong> to discover community-built workflows — filter by category, popularity, or recency.</li>
                <li>Click <strong className="text-white">Download</strong> on any workflow to install it directly into your workspace.</li>
                <li>Select the workflow from your sidebar and click <strong className="text-white">Run</strong> to launch it.</li>
              </ol>
            </div>
            <p className="text-gray-400 text-sm">
              Each workflow is a self-contained tool. Some connect to external APIs and may require configuration — check the workflow's description for setup instructions.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'exchange',
    title: 'Using the Exchange',
    icon: Store,
    items: [
      {
        id: 'browsing',
        title: 'Browsing & Downloading',
        icon: Search,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              The Exchange is ContextUI's marketplace for discovering and sharing workflows. Access it from the <strong className="text-white">Exchange</strong> tab in the app or at{' '}
              <a href="https://contextui.ai/exchange" className="text-emerald-400 hover:text-emerald-300 underline">contextui.ai/exchange</a>.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Finding Workflows</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Search</strong> — Use keywords to find workflows by name or description.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Filter by category</strong> — Browse by category to narrow results (see list below).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">One-click install</strong> — Hit Download and the workflow is added to your local ContextUI instance.</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Categories</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Generative AI</strong> <span className="text-gray-500">(gen_ai)</span> — LLM integrations, image generation, AI agents.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Developer Tools</strong> — Code review, debugging, embedded terminals.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Creative Tools</strong> — Music production, art creation, design utilities.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Productivity</strong> — Notes, converters, organizers, time management.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Games</strong> — Quizzes, simulations, interactive entertainment.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Data Tools</strong> — Charts, analysis, dashboards, data visualization.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">File Utilities</strong> — File management, batch operations, format conversion.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Image Processing</strong> — Image editing, conversion, manipulation.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Video Processing</strong> — Video editing, playback, transcoding.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">LLM</strong> — Local language models, text-to-speech, inference tools.</span>
                </li>
              </ul>
            </div>
          </>
        ),
      },
      {
        id: 'pro-subscription',
        title: 'Pro Subscription',
        icon: CreditCard,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              ContextUI Pro is an optional subscription that unlocks additional features for power users. Free users have full access to the app and public Exchange.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Pro Benefits</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">All official workflows free</strong> — Download any workflow published by the ContextUI team at no credit cost.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Private workflow uploads</strong> — Upload workflows visible only to you, with up to 1GB of storage. Private workflows skip the review queue.</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Pricing</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">$10/month</strong> — Monthly billing, cancel anytime.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">$100/year</strong> — Annual billing, save $20.</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              Manage your subscription from <strong className="text-gray-300">Account → Subscription</strong>. You can upgrade, downgrade, or cancel at any time.
            </p>
          </>
        ),
      },
      {
        id: 'credits',
        title: 'Credits System',
        icon: CreditCard,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Credits are the currency used to purchase paid workflows on the Exchange. Many workflows are free, but creators can set a price for premium content. Pro subscribers get all official workflows free without using credits.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">How Credits Work</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">1 credit = $0.01 USD</strong> — Simple, transparent pricing.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Add funds</strong> — Go to <strong className="text-gray-200">Account → Balance</strong> to purchase credits.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Packages</strong> — Choose from $10, $25, $50, or enter a custom amount.</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              Credits never expire. Your balance is displayed in your Account dashboard.
            </p>
          </>
        ),
      },
      {
        id: 'installing-workflows',
        title: 'Installing Downloaded Workflows',
        icon: FolderDown,
        content: (
          <>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">From the Desktop App</h4>
              <p className="text-gray-300 mb-2">Downloads from the Exchange tab inside the desktop app are fully automatic:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Files are downloaded, unzipped, and placed in the correct location automatically.</li>
                <li>The workflow appears in your sidebar immediately — no restart needed.</li>
                <li>Select it and click Run.</li>
              </ol>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">From the Website</h4>
              <p className="text-gray-300 mb-2">If you download a workflow from <span className="text-emerald-400">contextui.ai/exchange</span>, you'll need to install it manually:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Unzip the downloaded file.</li>
                <li>Move the workflow folder into your ContextUI workflows directory.</li>
                <li>The workflow will appear in your sidebar automatically.</li>
              </ol>
            </div>
          </>
        ),
      },
    ],
  },
  {
    id: 'creators',
    title: 'For Creators',
    icon: Code,
    items: [
      {
        id: 'publishing',
        title: 'Publishing a Workflow',
        icon: Upload,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Share your workflows with the community by publishing them on the Exchange. File naming matters — ContextUI uses conventions to discover and load your workflow.
            </p>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Required Files & Naming</h4>
              <p className="text-gray-400 text-sm mb-3">File names must follow these patterns exactly. The prefix (e.g. <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">MyWorkflow</code>) must be consistent across files.</p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">*Window.tsx</code> — Your main UI component. <strong className="text-white">Must end in <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">Window.tsx</code></strong>. Example: <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">MyWorkflowWindow.tsx</code></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">*Window.meta.json</code> — Must match your Window file name. Example: <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">MyWorkflowWindow.meta.json</code>. Uses <a href="https://phosphoricons.com" className="text-emerald-400 hover:text-emerald-300 underline" target="_blank" rel="noopener noreferrer">Phosphor icons</a> by name.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">description.txt</code> — The first line becomes the summary shown in Exchange listings. Remaining lines are the full description.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">*_server.py</code> <span className="text-gray-500">(optional)</span> — Python backend using FastAPI + uvicorn. Example: <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">MyWorkflow_server.py</code></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">requirements.txt</code> <span className="text-gray-500">(optional)</span> — Python dependencies for your server. Only needed if <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">_server.py</code> is included.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Example meta.json</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`{
  "icon": "ChartLine",
  "iconWeight": "regular",
  "color": "emerald",
  "server": {
    "file": "my_workflow_server.py",
    "port": 8100
  }
}

// Minimum required fields: icon, iconWeight, color
// Server block is only needed if you have a Python backend
// Colors: emerald, blue, purple, red, amber, cyan, pink, orange, green
// Icons: any Phosphor icon name (phosphoricons.com)`}
              </pre>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Tips for a Good Listing</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Include at least one <strong className="text-white">screenshot</strong> showing the workflow running — listings with screenshots get significantly more downloads.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Write a clear description covering what it <strong className="text-white">does</strong> and what it <strong className="text-white">doesn't</strong> do. Users appreciate honesty about limitations.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Don't ship dev artifacts</strong> — remove <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">PLAN.md</code>, test files, <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">node_modules</code>, <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">package-lock.json</code>, and log files before uploading.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Keep file sizes reasonable — don't bundle large data files. <strong className="text-white">100 MB total upload limit</strong>.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Visibility Options</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Public</strong> — Visible to everyone on the Exchange after review. This is the default.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Private</strong> <span className="text-indigo-400">(Pro only)</span> — Visible only to you. Private uploads skip the review queue and are available immediately. 1GB storage limit.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Review Process</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Go to <strong className="text-white">Exchange → Published → Upload</strong> and select your files.</li>
                <li>Fill in metadata (name, category, price) and choose visibility.</li>
                <li>Public submissions are <strong className="text-white">reviewed by the team</strong> before going live. Private workflows are available immediately.</li>
                <li>Once approved, public workflows appear on the Exchange for everyone to discover.</li>
              </ol>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Common Rejection Reasons</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Not a ContextUI workflow (e.g. just a standalone script with no Window component).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Missing required files — no <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">*Window.tsx</code> or <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">*.meta.json</code>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Dev artifacts included (<code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">node_modules</code>, logs, test files).</span>
                </li>
              </ul>
            </div>
          </>
        ),
      },
      {
        id: 'stripe-connect',
        title: 'Setting Up Stripe Connect',
        icon: LinkIcon,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              To receive payments for your paid workflows, connect a Stripe account. This is how you get paid — directly to your bank account.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Connect Stripe</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Navigate to <strong className="text-white">Account → Earnings → Connect Stripe</strong>.</li>
                <li>Create a new Stripe account or link an existing one.</li>
                <li>Complete the Stripe onboarding process (identity verification, bank details).</li>
                <li>Once connected, you'll <strong className="text-white">automatically receive 70%</strong> of every sale.</li>
              </ol>
            </div>
          </>
        ),
      },
      {
        id: 'pricing-workflow',
        title: 'Pricing Your Workflow',
        icon: DollarSign,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              You control the pricing for your workflows. Set a price during the upload process.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Pricing Details</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Set your price in <strong className="text-white">USD</strong> during the upload process.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Free workflows</strong> tend to get significantly more downloads and visibility.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Paid workflows</strong> earn you 70% of each sale. The platform retains 30%.</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              You can change your workflow's price at any time by updating its listing.
            </p>
          </>
        ),
      },
      {
        id: 'tracking-earnings',
        title: 'Tracking Earnings',
        icon: BarChart3,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Keep track of your sales and revenue from the Account dashboard.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Earnings Dashboard</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Go to <strong className="text-white">Account → Earnings</strong> to view your summary.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>See <strong className="text-white">total earned</strong>, <strong className="text-white">transferred</strong>, and <strong className="text-white">pending</strong> amounts at a glance.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>View <strong className="text-white">transaction history</strong> broken down per workflow.</span>
                </li>
              </ul>
            </div>
          </>
        ),
      },
      {
        id: 'updating-workflow',
        title: 'Updating Your Workflow',
        icon: RefreshCw,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Keep your workflows up to date by uploading new versions as you improve them.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Update Process</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Go to <strong className="text-white">Published</strong> and select the workflow you want to update.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Upload new files — this will trigger a <strong className="text-white">re-review</strong> before the update goes live.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Metadata-only updates</strong> (description, price, category) don't require re-review and take effect immediately.</span>
                </li>
              </ul>
            </div>
          </>
        ),
      },
    ],
  },
  {
    id: 'ai-integration',
    title: 'AI in Workflows',
    icon: Bot,
    items: [
      {
        id: 'ai-quick-start',
        title: 'Quick Start',
        icon: Zap,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Any workflow running inside ContextUI can access <strong className="text-white">340+ AI models</strong> (text, vision, image generation, audio, and more) through a single API endpoint. The user's credits are charged automatically — no API keys or billing code needed in your workflow.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Step 1 — Get the Auth Token</h4>
              <p className="text-gray-400 text-sm mb-3">Read the token from localStorage, with a fallback to Electron IPC for the desktop app:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`// Synchronous read — works after initial sync
const getAuthToken = (): string | null => {
  try {
    const session = localStorage.getItem('workos_session');
    if (session) {
      const parsed = JSON.parse(session);
      return parsed.accessToken || null;
    }
  } catch {}
  return null;
};

// Call once on mount to sync token from Electron into localStorage
const syncAuthFromDesktop = async () => {
  if (getAuthToken()) return; // Already available
  try {
    const ipc = (window as any).require?.('electron')?.ipcRenderer;
    if (ipc) {
      const authData = await ipc.invoke('get-auth-data');
      if (authData?.accessToken) {
        localStorage.setItem('workos_session',
          JSON.stringify({ accessToken: authData.accessToken })
        );
      }
    }
  } catch {}
};

// Run in a useEffect on mount
useEffect(() => { syncAuthFromDesktop(); }, []);`}
              </pre>
              <p className="text-gray-500 text-xs mt-3">The desktop app stores auth tokens securely via Electron — they aren't in localStorage by default. The sync function fetches the token via IPC and writes it to localStorage so <code className="bg-gray-800 px-1 py-0.5 rounded text-gray-300 text-xs">getAuthToken()</code> works everywhere.</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Step 2 — Call the AI Endpoint</h4>
              <p className="text-gray-400 text-sm mb-3">Send a POST request with the model ID and messages. That's the entire integration:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`const token = getAuthToken();
const response = await fetch(
  'https://contextui.ai/.netlify/functions/ai-chat',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${token}\`
    },
    body: JSON.stringify({
      model: 'anthropic/claude-3.5-sonnet',
      messages: [
        { role: 'user', content: 'Hello!' }
      ],
      max_tokens: 4096
    })
  }
);

const data = await response.json();
const reply = data.choices[0].message.content;

// Credit info is in data._contextui
// data._contextui.credits_charged
// data._contextui.credits_remaining`}
              </pre>
            </div>
            <p className="text-gray-400 text-sm">
              Credits are reserved before the request, charged for actual usage, and any overage is refunded automatically. Free models (<code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">is_free: true</code>) work without any credits at all.
            </p>
          </>
        ),
      },
      {
        id: 'ai-full-example',
        title: 'Full Example — makeApiCall',
        icon: Code,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Here's a reusable helper function you can drop into any workflow. It also supports a <strong className="text-white">BYO Key</strong> mode so users can optionally use their own OpenRouter API key.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Reusable API Helper</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`const CONTEXTUI_API = 'https://contextui.ai/.netlify/functions';

const makeApiCall = async (
  modelId: string,
  messages: { role: string; content: any }[],
  options: { max_tokens?: number } = {}
) => {
  const { max_tokens = 4096 } = options;
  const token = getAuthToken();
  if (!token) throw new Error('Not logged in');

  const body = JSON.stringify({ model: modelId, messages, max_tokens });
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': \`Bearer \${token}\`
  };

  let response = await fetch(\`\${CONTEXTUI_API}/ai-chat\`, {
    method: 'POST', headers, body
  });

  // Token expired — refresh via IPC and retry once
  if (response.status === 401) {
    try {
      const ipc = (window as any).require?.('electron')?.ipcRenderer;
      if (ipc) {
        const authData = await ipc.invoke('get-auth-data');
        if (authData?.accessToken) {
          localStorage.setItem('workos_session',
            JSON.stringify({ accessToken: authData.accessToken })
          );
          response = await fetch(\`\${CONTEXTUI_API}/ai-chat\`, {
            method: 'POST',
            headers: { ...headers, 'Authorization': \`Bearer \${authData.accessToken}\` },
            body
          });
        }
      }
    } catch {}
  }

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Request failed');

  return {
    content: data.choices?.[0]?.message?.content || '',
    usage: data.usage,
    credits: data._contextui?.credits_charged
  };
};`}
              </pre>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Usage Examples</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`// Simple text chat
const result = await makeApiCall(
  'anthropic/claude-3.5-sonnet',
  [{ role: 'user', content: 'Explain quantum computing' }]
);
console.log(result.content);

// Vision — send an image for analysis
const result = await makeApiCall(
  'openai/gpt-4o',
  [{
    role: 'user',
    content: [
      { type: 'text', text: 'What is in this image?' },
      { type: 'image_url', image_url: { url: base64DataUrl } }
    ]
  }]
);

// Multi-turn conversation
const result = await makeApiCall(
  'google/gemini-2.0-flash-001',
  [
    { role: 'user', content: 'Hi' },
    { role: 'assistant', content: 'Hello!' },
    { role: 'user', content: 'What is 2+2?' }
  ]
);`}
              </pre>
            </div>
          </>
        ),
      },
      {
        id: 'ai-models-list',
        title: 'Available Models',
        icon: List,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Fetch the list of enabled models from the public endpoint. No auth required.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">List Models</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700 mb-4">
{`// Get all enabled models
const res = await fetch(
  'https://contextui.ai/.netlify/functions/ai-models'
);
const { models } = await res.json();

// Filter by capability
const res = await fetch(
  'https://contextui.ai/.netlify/functions/ai-models?capabilities=vision'
);

// Filter by tier
const res = await fetch(
  'https://contextui.ai/.netlify/functions/ai-models?tier=budget'
);

// Filter by provider
const res = await fetch(
  'https://contextui.ai/.netlify/functions/ai-models?provider=anthropic'
);`}
              </pre>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Model Object</h4>
              <p className="text-gray-400 text-sm mb-3">Each model in the response includes:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Field</th>
                      <th className="pb-2 text-emerald-400 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">id</td><td className="py-2">Model ID to pass to ai-chat (e.g. <code className="text-emerald-400">anthropic/claude-3.5-sonnet</code>)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">name</td><td className="py-2">Display name</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">provider</td><td className="py-2">Provider name (anthropic, openai, google, etc.)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">is_free</td><td className="py-2">True if the model costs zero credits</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">capabilities</td><td className="py-2">Array: text, vision, code, audio, image-gen, etc.</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">tier</td><td className="py-2">budget, standard, premium, or free</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">context_length</td><td className="py-2">Max context window in tokens</td></tr>
                    <tr><td className="py-2 pr-4 font-mono text-xs text-gray-200">credits_per_1k_input / output</td><td className="py-2">Cost per 1K tokens</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Models are synced from OpenRouter automatically. Pricing includes a 30% platform margin. The response is cached for 5 minutes.
            </p>
          </>
        ),
      },
      {
        id: 'ai-streaming',
        title: 'Streaming Responses',
        icon: Radio,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              For real-time token-by-token output, set <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">stream: true</code> in your request. The response is a standard <strong className="text-white">Server-Sent Events (SSE)</strong> stream.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Streaming Example</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`const token = getAuthToken();

const response = await fetch(
  'https://contextui.ai/.netlify/functions/ai-chat',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${token}\`
    },
    body: JSON.stringify({
      model: 'anthropic/claude-3.5-sonnet',
      messages: [{ role: 'user', content: 'Write a poem' }],
      max_tokens: 4096,
      stream: true  // Enable streaming
    })
  }
);

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const chunk = decoder.decode(value, { stream: true });
  const lines = chunk.split('\\n');

  for (const line of lines) {
    if (line.startsWith('data: ') && line !== 'data: [DONE]') {
      const data = JSON.parse(line.slice(6));
      const token = data.choices?.[0]?.delta?.content;
      if (token) {
        // Append token to your UI
        process.stdout.write(token);
      }
    }
  }
}`}
              </pre>
            </div>
            <p className="text-gray-400 text-sm">
              Credits are reserved before the stream starts and finalized after it ends. The billing works the same as non-streaming requests.
            </p>
          </>
        ),
      },
      {
        id: 'ai-request-options',
        title: 'Request Options',
        icon: Code,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              The full set of parameters you can pass in the request body:
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Parameter</th>
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Required</th>
                      <th className="pb-2 text-emerald-400 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">model</td><td className="py-2 pr-4">Yes</td><td className="py-2">Model ID from the <code className="text-emerald-400">/ai-models</code> endpoint</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">messages</td><td className="py-2 pr-4">Yes</td><td className="py-2">Array of message objects with <code className="text-emerald-400">role</code> and <code className="text-emerald-400">content</code></td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">max_tokens</td><td className="py-2 pr-4">No</td><td className="py-2">Max tokens to generate (default: 4096)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">temperature</td><td className="py-2 pr-4">No</td><td className="py-2">Sampling temperature (0–2)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">top_p</td><td className="py-2 pr-4">No</td><td className="py-2">Nucleus sampling threshold</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">stream</td><td className="py-2 pr-4">No</td><td className="py-2">Enable SSE streaming (default: false)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">tools</td><td className="py-2 pr-4">No</td><td className="py-2">Tool/function definitions for tool use</td></tr>
                    <tr><td className="py-2 pr-4 font-mono text-xs text-gray-200">tool_choice</td><td className="py-2 pr-4">No</td><td className="py-2">Tool selection strategy</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Headers</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Header</th>
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Required</th>
                      <th className="pb-2 text-emerald-400 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">Authorization</td><td className="py-2 pr-4">Yes</td><td className="py-2"><code className="text-emerald-400">Bearer &lt;token&gt;</code> from localStorage</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">Content-Type</td><td className="py-2 pr-4">Yes</td><td className="py-2"><code className="text-emerald-400">application/json</code></td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">X-Workflow-Id</td><td className="py-2 pr-4">No</td><td className="py-2">Your workflow ID for usage tracking and analytics</td></tr>
                    <tr><td className="py-2 pr-4 font-mono text-xs text-gray-200">X-Session-Id</td><td className="py-2 pr-4">No</td><td className="py-2">Session ID to group related requests</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Response Format</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`{
  "choices": [{
    "message": {
      "role": "assistant",
      "content": "The AI response text..."
    }
  }],
  "usage": {
    "prompt_tokens": 50,
    "completion_tokens": 200
  },
  "_contextui": {
    "is_free": false,
    "credits_charged": 0.0234,
    "credits_remaining": 9.42,
    "latency_ms": 1200
  }
}`}
              </pre>
            </div>
          </>
        ),
      },
      {
        id: 'ai-error-handling',
        title: 'Error Handling',
        icon: AlertTriangle,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              Handle these status codes in your workflow to give users clear feedback:
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Status</th>
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Meaning</th>
                      <th className="pb-2 text-emerald-400 font-semibold">What to Do</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-yellow-400">402</td><td className="py-2 pr-4">Insufficient credits</td><td className="py-2">Show <code className="text-emerald-400">data.top_up_url</code> so the user can add funds</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-yellow-400">429</td><td className="py-2 pr-4">Rate limited</td><td className="py-2">Wait and retry — limit is 60 requests per minute</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-yellow-400">400</td><td className="py-2 pr-4">Bad request</td><td className="py-2">Check model ID exists and messages array is valid</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-yellow-400">401</td><td className="py-2 pr-4">Not authenticated</td><td className="py-2">Token expired — refresh via IPC and retry once (see below)</td></tr>
                    <tr><td className="py-2 pr-4 font-mono text-xs text-yellow-400">502</td><td className="py-2 pr-4">AI provider error</td><td className="py-2">Upstream model error — try a different model or retry</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Handling Insufficient Credits</h4>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`const response = await fetch(url, { ... });

if (response.status === 402) {
  const err = await response.json();
  // err.required — credits needed for this request
  // err.current_balance — user's current balance
  // err.top_up_url — link to add funds
  alert(
    \`Not enough credits. Balance: \${err.current_balance}. \` +
    \`Top up at: \${err.top_up_url}\`
  );
  return;
}

if (!response.ok) {
  const err = await response.json();
  throw new Error(err.error || \`Request failed: \${response.status}\`);
}`}
              </pre>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Handling Expired Tokens (401)</h4>
              <p className="text-gray-400 text-sm mb-3">Tokens expire after ~1 hour. On 401, re-fetch from Electron IPC and retry once:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`if (response.status === 401) {
  // Re-fetch token from Electron (triggers auto-refresh)
  const ipc = (window as any).require?.('electron')?.ipcRenderer;
  if (ipc) {
    const authData = await ipc.invoke('get-auth-data');
    if (authData?.accessToken) {
      localStorage.setItem('workos_session',
        JSON.stringify({ accessToken: authData.accessToken })
      );
      // Retry with fresh token
      const retry = await fetch(url, {
        ...opts,
        headers: {
          ...opts.headers,
          'Authorization': \`Bearer \${authData.accessToken}\`
        }
      });
      if (retry.ok) return await retry.json();
    }
  }
  // Refresh failed — user needs to sign in again
  alert('Session expired. Please sign in again.');
}`}
              </pre>
            </div>
          </>
        ),
      },
    ],
  },
  {
    id: 'api',
    title: 'API & Automation',
    icon: Key,
    items: [
      {
        id: 'api-key',
        title: 'Getting an API Key',
        icon: Key,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              API keys allow you to interact with ContextUI's services programmatically.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Generate Your Key</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Sign in and go to <strong className="text-white">Account → API Keys</strong>.</li>
                <li>Click <strong className="text-white">Generate</strong> to create a new API key.</li>
                <li>Copy and store your key securely — it won't be shown again.</li>
                <li>Use the key in the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">Authorization</code> header of your API requests.</li>
              </ol>
            </div>
          </>
        ),
      },
      {
        id: 'exchange-api',
        title: 'Exchange API',
        icon: Code,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              The Exchange API provides full programmatic access to the marketplace. All endpoints are under <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">https://contextui.ai/.netlify/functions/</code> and require an <code className="bg-gray-800 px-1.5 py-0.5 rounded text-emerald-400 text-xs">Authorization: Bearer YOUR_KEY</code> header.
            </p>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Endpoint Reference</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-emerald-400 font-semibold pr-4">Endpoint</th>
                      <th className="pb-2 text-emerald-400 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace?search=query&category=cat</td><td className="py-2">Browse & search public workflows</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace?id=UUID</td><td className="py-2">Get workflow details</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace-download?id=UUID</td><td className="py-2">Get download URLs (Pro: official free)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace-comments?listingId=UUID</td><td className="py-2">Get comments on a listing</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-comments</td><td className="py-2">Post a comment</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-like</td><td className="py-2">Toggle like on a listing</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace-my-workflows</td><td className="py-2">List your workflows (includes private)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET marketplace-my-downloads</td><td className="py-2">List your downloaded workflows</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-upload-init</td><td className="py-2">Initialize upload (get presigned S3 URLs)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-upload-complete</td><td className="py-2">Complete upload (visibility: public|private)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-webapp-upload</td><td className="py-2">Initialize web app listing</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-webapp-complete</td><td className="py-2">Complete web app listing</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST marketplace-delete</td><td className="py-2">Delete your listing (soft or hard)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET get-subscription</td><td className="py-2">Get subscription status (tier: free|pro)</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST upgrade-subscription</td><td className="py-2">Upgrade from Free to Pro</td></tr>
                    <tr className="border-b border-gray-800"><td className="py-2 pr-4 font-mono text-xs text-gray-200">GET credits-balance</td><td className="py-2">Check your credit balance</td></tr>
                    <tr><td className="py-2 pr-4 font-mono text-xs text-gray-200">POST credits-deposit</td><td className="py-2">Create checkout session for credits</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Examples</h4>
              <p className="text-gray-400 text-sm mb-3">Search for workflows:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700 mb-4">
{`curl -H "Authorization: Bearer YOUR_KEY" \\
  "https://contextui.ai/.netlify/functions/marketplace?search=trading"`}
              </pre>
              <p className="text-gray-400 text-sm mb-3">Get workflow details by ID:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700 mb-4">
{`curl -H "Authorization: Bearer YOUR_KEY" \\
  "https://contextui.ai/.netlify/functions/marketplace?id=WORKFLOW_UUID"`}
              </pre>
              <p className="text-gray-400 text-sm mb-3">Download a workflow:</p>
              <pre className="bg-gray-900 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto border border-gray-700">
{`curl -H "Authorization: Bearer YOUR_KEY" \\
  "https://contextui.ai/.netlify/functions/marketplace-download?id=WORKFLOW_UUID"`}
              </pre>
            </div>
          </>
        ),
      },
      {
        id: 'mcp',
        title: 'MCP Integration',
        icon: Cpu,
        content: (
          <>
            <p className="text-gray-300 mb-4">
              ContextUI supports the <strong className="text-white">Model Context Protocol (MCP)</strong>, enabling AI agents to build and run workflows programmatically.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Compatible Agents</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong className="text-white">Claude Code</strong> — Anthropic's CLI coding agent.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Any agent or IDE that supports the <strong className="text-white">Model Context Protocol (MCP)</strong> standard.</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              With MCP, agents can create workflows from natural language descriptions, install packages from the Exchange, and execute workflows — all without manual interaction.
            </p>
          </>
        ),
      },
    ],
  },
];

const Docs = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);
  const location = useLocation();

  // Expand all sections by default
  useEffect(() => {
    const initialSections = {};
    sections.forEach((s) => {
      initialSections[s.id] = true;
    });
    setExpandedSections(initialSections);
  }, []);

  // Handle hash-based navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      // Find and expand the parent section
      for (const section of sections) {
        const item = section.items.find((i) => i.id === hash);
        if (item) {
          setExpandedSections((prev) => ({ ...prev, [section.id]: true }));
          setExpandedItems((prev) => ({ ...prev, [hash]: true }));
          setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
          break;
        }
      }
    }
  }, [location.hash]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const toggleItem = (itemId) => {
    setExpandedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const scrollToItem = (sectionId, itemId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: true }));
    setExpandedItems((prev) => ({ ...prev, [itemId]: true }));
    setMobileNavOpen(false);
    setTimeout(() => {
      const el = document.getElementById(itemId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const Sidebar = () => (
    <nav className="space-y-1">
      {sections.map((section) => {
        const Icon = section.icon;
        return (
          <div key={section.id}>
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Icon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
              <span className="flex-1 text-left">{section.title}</span>
              {expandedSections[section.id] ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
            {expandedSections[section.id] && (
              <div className="ml-6 border-l border-gray-800 pl-3 mt-1 mb-2 space-y-0.5">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToItem(section.id, item.id)}
                    className="block w-full text-left px-2 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800/50 rounded transition-colors"
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="bg-black min-h-screen text-white">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <BookOpen className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Documentation</h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Everything you need to get started with ContextUI, publish workflows, and integrate with our API.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile nav toggle */}
        <div className="lg:hidden mb-6">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-sm font-medium text-white"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-emerald-400" />
              Navigation
            </span>
            {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          {mobileNavOpen && (
            <div className="mt-2 bg-gray-900 border border-gray-700 rounded-lg p-4">
              <Sidebar />
            </div>
          )}
        </div>

        <div className="flex gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-32 max-h-[calc(100vh-10rem)] overflow-y-auto pr-2">
              <Sidebar />
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0" ref={contentRef}>
            <div className="space-y-8">
              {sections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <div key={section.id} className="space-y-4">
                    {/* Section header */}
                    <div className="flex items-center gap-3 pb-2 border-b border-gray-800">
                      <SectionIcon className="h-6 w-6 text-emerald-400" />
                      <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                    </div>

                    {/* Items */}
                    <div className="space-y-3">
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        const isExpanded = expandedItems[item.id];
                        return (
                          <div
                            key={item.id}
                            id={item.id}
                            className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden scroll-mt-36"
                          >
                            <button
                              onClick={() => toggleItem(item.id)}
                              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-800/50 transition-colors"
                            >
                              <ItemIcon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                              <span className="flex-1 font-medium text-white">{item.title}</span>
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                            {isExpanded && (
                              <div className="px-5 pb-5 pt-1 border-t border-gray-800">
                                <div className="pt-4">{item.content}</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer CTA */}
            <div className="mt-12 mb-8 bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
              <p className="text-gray-400 mb-4">
                Reach out to our support team and we'll help you out.
              </p>
              <a
                href="/support"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors"
              >
                Contact Support
              </a>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Docs;
